import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Building2, Landmark, Package, TrendingUp, Briefcase, Users,
  BarChart3, Wallet, ShieldAlert, Sparkles,
} from "lucide-react";
import { ProposalStepIndicator } from "../components/proposals/ProposalStepIndicator";
import { CurrencySelector } from "../components/ui/CurrencySelector";
import { IndustrySelector } from "../components/ui/IndustrySelector";
import {
  getCurrencyDisplayPrefix,
  normalizeCurrencyCode,
} from "../utils/currencies";
import {
  INDUSTRY_OTHER,
  normalizeIndustryFields,
  resolveIndustry,
} from "../utils/industries";
import "./BusinessPlanWizardPage.css";

const DRAFT_VERSION = "v1";
const AUTOSAVE_MS = 30000;
const WIZARD_STEP_COUNT = 10;
const GENERATION_POLL_INTERVAL_MS = 4000;
const GENERATION_POLL_TIMEOUT_MS = 15000;
const GENERATION_MAX_POLL_ATTEMPTS = 120;
const GENERATION_JOB_CREATE_TIMEOUT_MS = 30000;

const STEPS = [
  { id: 1, title: "Business Info", icon: Building2, hint: "Company details" },
  { id: 2, title: "Funding Request", icon: Landmark, hint: "Amount & purpose" },
  { id: 3, title: "Products & Services", icon: Package, hint: "What you offer" },
  { id: 4, title: "Market & Competition", icon: TrendingUp, hint: "Market analysis" },
  { id: 5, title: "Business Model", icon: Briefcase, hint: "Revenue & sales" },
  { id: 6, title: "Management Team", icon: Users, hint: "Key people" },
  { id: 7, title: "Financial Projections", icon: BarChart3, hint: "3-year forecasts" },
  { id: 8, title: "Repayment & Collateral", icon: Wallet, hint: "Loan repayment" },
  { id: 9, title: "Risk & Mitigation", icon: ShieldAlert, hint: "Key risks" },
  { id: 10, title: "Suggestions & Generate", icon: Sparkles, hint: "Draft & export" },
];

const FUNDING_PURPOSES = [
  "Working Capital",
  "Equipment Purchase",
  "Business Expansion",
  "Inventory Financing",
  "Startup Capital",
  "Operational Expenses",
  "Asset Acquisition",
  "Franchise Expansion",
  "Debt Refinancing",
];

const LEGAL_STRUCTURES = [
  "Sole Proprietorship", "Partnership", "Limited Liability Company (LLC)",
  "Private Limited Company (Ltd)", "Public Limited Company (PLC)",
  "Non-Profit Organization", "Cooperative", "Other",
];

const BUSINESS_STAGES = [
  { value: "Pre-Revenue / Startup", label: "Pre-Revenue / Startup" },
  { value: "Early Stage", label: "Early Stage (< 1 year)" },
  { value: "Growth Stage", label: "Growth Stage (1–3 years)" },
  { value: "Established", label: "Established (3–5 years)" },
  { value: "Mature Business", label: "Mature Business (5+ years)" },
];

/** Map stored value or legacy full dropdown label → clean value for PDF/AI. */
function normalizeBusinessStage(stage) {
  if (!stage) return "";
  const match = BUSINESS_STAGES.find((s) => s.value === stage || s.label === stage);
  if (match) return match.value;
  return String(stage).replace(/\s*\([^)]*\)\s*$/, "").trim();
}

const TEMPLATES = [
  { value: "classic", label: "Classic", desc: "Clean professional layout" },
  { value: "modern", label: "Modern", desc: "Contemporary bold design" },
  { value: "premium", label: "Premium", desc: "Executive premium finish" },
];

const BP_LAYOUT_VALUES = TEMPLATES.map((t) => t.value);

function resolveBpLayoutFromTemplate(template) {
  if (!template) return "classic";
  const layout = String(template.layout || "").toLowerCase().trim();
  if (BP_LAYOUT_VALUES.includes(layout)) return layout;
  const name = String(template.name || "").toLowerCase();
  if (name.includes("premium")) return "premium";
  if (name.includes("modern")) return "modern";
  if (name.includes("classic")) return "classic";
  return "classic";
}

const LIKELIHOODS = ["Low", "Medium", "High"];
const IMPACTS = ["Low", "Medium", "High", "Critical"];

const GENERIC_PLACEHOLDERS = {
  revenue_model: "e.g. Product/service sales, subscription fees, project contracts, retainer agreements...",
  pricing_strategy: "e.g. Cost-plus pricing, competitive market rates, tiered packages, volume discounts...",
  sales_channels: "e.g. Direct sales, online platform, distributor partnerships, referrals...",
  customer_acquisition: "e.g. Referrals, digital marketing, trade shows, cold outreach, community engagement...",
};

const INDUSTRY_PLACEHOLDERS = {
  "Logistics & Supply Chain": {
    revenue_model: "e.g. Per-trip billing, monthly haulage contracts, last-mile delivery fees...",
    pricing_strategy: "e.g. Distance-based pricing, weight/volume pricing, contract retainers...",
    sales_channels: "e.g. Direct B2B outreach, freight brokers, logistics platforms like Kobo360...",
    customer_acquisition: "e.g. Cold outreach to manufacturers, referrals from existing clients, trade associations...",
  },
  "Transportation & Haulage": {
    revenue_model: "e.g. Per-trip billing, monthly haulage contracts, fleet leasing income...",
    pricing_strategy: "e.g. Distance-based pricing, weight/volume pricing, contract retainers...",
    sales_channels: "e.g. Direct B2B outreach, freight brokers, haulage platforms...",
    customer_acquisition: "e.g. Cold outreach to manufacturers, referrals from existing clients, trade associations...",
  },
  "Retail & E-commerce": {
    revenue_model: "e.g. Product sales instore and online, wholesale to resellers...",
    pricing_strategy: "e.g. Cost-plus pricing, seasonal discounts, bundle deals...",
    sales_channels: "e.g. Physical store, Instagram/WhatsApp commerce, Jumia/Konga listings...",
    customer_acquisition: "e.g. Social media marketing, walk-in traffic, influencer promotions...",
  },
  "Agriculture & Agribusiness": {
    revenue_model: "e.g. Crop sales to offtakers, contract farming agreements, value-added processing...",
    pricing_strategy: "e.g. Market rate pricing, forward contracts, premium pricing for organic produce...",
    sales_channels: "e.g. Direct farm sales, commodity markets, supermarket supply agreements...",
    customer_acquisition: "e.g. Agro-cooperatives, government procurement programs, food processors...",
  },
  "Construction & Real Estate": {
    revenue_model: "e.g. Project-based contracts, bill of quantities, property development income...",
    pricing_strategy: "e.g. BOQ-based quoting, fixed-price contracts, cost-plus contracts...",
    sales_channels: "e.g. Tender bidding, direct client relationships, real estate developer networks...",
    customer_acquisition: "e.g. Government tenders, real estate developers, referrals from architects...",
  },
  "ICT & Software Development": {
    revenue_model: "e.g. SaaS subscriptions, one-time software licenses, implementation fees...",
    pricing_strategy: "e.g. Tiered subscription plans, usage-based pricing, freemium model...",
    sales_channels: "e.g. Direct sales, app stores, SaaS marketplaces, reseller partnerships...",
    customer_acquisition: "e.g. Content marketing, LinkedIn outreach, product demos, free trials...",
  },
  "Technology (General)": {
    revenue_model: "e.g. Product sales, service contracts, licensing fees...",
    pricing_strategy: "e.g. Tiered pricing, competitive market rates, project-based quotes...",
    sales_channels: "e.g. Direct sales, online platform, distributor partnerships...",
    customer_acquisition: "e.g. Digital marketing, referrals, trade shows, demos...",
  },
  "Manufacturing & Production": {
    revenue_model: "e.g. Bulk product sales, OEM manufacturing contracts, direct-to-retail supply...",
    pricing_strategy: "e.g. Cost-plus pricing, volume discounts, long-term supply contracts...",
    sales_channels: "e.g. Distributor networks, direct factory sales, export markets...",
    customer_acquisition: "e.g. Trade fairs, distributor partnerships, export promotion councils...",
  },
  "Healthcare & Pharmaceuticals": {
    revenue_model: "e.g. Consultation fees, prescription sales, HMO billing, lab services...",
    pricing_strategy: "e.g. HMO-negotiated rates, private pay pricing, package health plans...",
    sales_channels: "e.g. Walk-in patients, HMO referrals, telemedicine platform...",
    customer_acquisition: "e.g. Doctor referrals, community health outreach, HMO enrollment...",
  },
  "Food & Beverage": {
    revenue_model: "e.g. Dine-in revenue, takeout/delivery orders, catering contracts...",
    pricing_strategy: "e.g. Menu-based pricing, event catering packages, delivery surcharges...",
    sales_channels: "e.g. Restaurant floor, Bolt Food/Glovo delivery, corporate catering...",
    customer_acquisition: "e.g. Google Maps listing, food bloggers, office lunch delivery deals...",
  },
  "Hospitality & Tourism": {
    revenue_model: "e.g. Room bookings, event hosting, restaurant/bar revenue, tours...",
    pricing_strategy: "e.g. Seasonal pricing, corporate rates, group booking discounts...",
    sales_channels: "e.g. Direct booking, OTAs like Booking.com, travel agents...",
    customer_acquisition: "e.g. OTA listings, TripAdvisor presence, corporate travel desks...",
  },
};

const SWOT_FIELD_NAMES = ["strengths", "weaknesses", "opportunities", "threats"];

const SWOT_LEGACY_FIELD_MAP = {
  strengths: "swot_strengths",
  weaknesses: "swot_weaknesses",
  opportunities: "swot_opportunities",
  threats: "swot_threats",
};

/** Normalize SWOT draft API response → wizard state keys (strengths, weaknesses, …). */
function mapSwotDraftToState(result = {}) {
  return {
    strengths: result.strengths ?? result.swot_strengths ?? "",
    weaknesses: result.weaknesses ?? result.swot_weaknesses ?? "",
    opportunities: result.opportunities ?? result.swot_opportunities ?? "",
    threats: result.threats ?? result.swot_threats ?? "",
  };
}

/** Migrate legacy swot_* keys from saved drafts / rerun payloads. */
function migrateSwotWizardFields(source = {}) {
  const next = { ...source };
  SWOT_FIELD_NAMES.forEach((key) => {
    const legacy = SWOT_LEGACY_FIELD_MAP[key];
    if (!String(next[key] || "").trim() && String(next[legacy] || "").trim()) {
      next[key] = next[legacy];
    }
  });
  return next;
}

const NARRATIVE_KEYS = [
  "executive_summary", "mission_statement", "vision_statement", "business_description",
  "industry_trends", "competitive_landscape", "competitive_advantage",
  ...SWOT_FIELD_NAMES,
  "funding_justification", "financial_assumptions", "repayment_strategy",
  "risk_intro", "closing_statement",
];

const DRAFT_ALL_JOBS = [
  { id: "executive_summary", section: "executive_summary", fields: ["executive_summary"] },
  { id: "business_description", section: "business_description", fields: ["business_description"] },
  { id: "mission_vision", section: "mission_vision", fields: ["mission_statement", "vision_statement"] },
  { id: "market_analysis", section: "market_analysis", fields: ["industry_trends", "competitive_landscape", "competitive_advantage"] },
  { id: "swot", section: "swot", fields: SWOT_FIELD_NAMES },
  { id: "funding_justification", section: "funding_justification", fields: ["funding_justification"] },
  { id: "financial_assumptions", section: "financial_assumptions", fields: ["financial_assumptions"] },
  { id: "repayment_strategy", section: "repayment", fields: ["repayment_strategy"] },
  { id: "risk_intro", section: "risk_intro", fields: ["risk_intro"] },
  { id: "closing_statement", section: "closing_statement", fields: ["closing_statement"] },
];

const parseAmount = (val) => {
  const n = parseFloat(String(val || "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const parseRepaymentMonths = (val) => {
  const text = String(val || "").trim().toLowerCase();
  if (!text) return 0;
  const match = text.match(/([\d.]+)/);
  if (!match) return 0;
  const num = parseFloat(match[1]);
  if (text.includes("year") || text.includes("yr")) return Math.round(num * 12);
  return Math.round(num);
};

const parseInterestRate = (val) => {
  const text = String(val || "").trim();
  if (!text) return 0;
  const match = text.match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : 0;
};

const calcMonthlyInstallment = (principal, annualRatePct, months) => {
  if (principal <= 0 || months <= 0) return 0;
  const r = (annualRatePct / 100) / 12;
  if (r <= 0) return principal / months;
  const factor = (1 + r) ** months;
  return principal * (r * factor) / (factor - 1);
};

const projectRevenueYears = (fin, currentRevenue = "") => {
  const growth = parseAmount(fin.growth_pct) / 100;
  const multiplier = 1 + (Number.isFinite(growth) ? growth : 0);
  const baseline = parseAmount(fin.current_annual_revenue) || parseAmount(currentRevenue);
  const y1 = baseline > 0 ? Math.round(baseline * multiplier) : 0;
  const y2 = y1 > 0 ? Math.round(y1 * multiplier) : 0;
  const y3 = y2 > 0 ? Math.round(y2 * multiplier) : 0;
  return { y1, y2, y3, growthPct: parseAmount(fin.growth_pct) };
};

const getRevenueWarnings = (fin, currentRevenue) => {
  const warnings = [];
  const { y1, y2, y3, growthPct } = projectRevenueYears(fin, currentRevenue);

  if (growthPct > 100) {
    warnings.push("⚠ Growth above 100% per year looks very aggressive. Lenders may question such projections.");
  }
  if (y1 > 0 && y2 > y1 * 3) {
    warnings.push("⚠ Year 2 growth looks very aggressive. Lenders may question projections above 200% growth.");
  }
  if (y2 > 0 && y3 > y2 * 3) {
    warnings.push("⚠ Year 3 growth looks very aggressive. Lenders may question projections above 200% growth.");
  }

  // COGS vs revenue check
  const cogs = parseAmount(fin.annual_cost_of_sales);
  const baseline = parseAmount(fin.current_annual_revenue) || parseAmount(currentRevenue);
  if (cogs > 0 && baseline > 0 && cogs >= baseline) {
    warnings.push("⚠ Cost of goods sold equals or exceeds current revenue. Your gross profit will be zero or negative — lenders will question this.");
  }
  if (cogs > 0 && baseline > 0 && cogs > baseline * 0.85) {
    warnings.push("⚠ Cost of goods sold is above 85% of revenue. This leaves a very thin gross margin — consider reviewing your figures.");
  }

  // Salary sanity check
  const monthlySalary = parseAmount(fin.monthly_salary);
  const monthlyRevenue = baseline / 12;
  if (monthlySalary > 0 && monthlyRevenue > 0 && monthlySalary > monthlyRevenue * 0.5) {
    warnings.push("⚠ Monthly salary exceeds 50% of monthly revenue. This may raise concerns with lenders about operational sustainability.");
  }

  return warnings;
};

const getSalaryPerEmployeeWarning = (fin, employeeCount, currency = "₦") => {
  const salariesWages = parseAmount(fin.monthly_salary) * 12;
  const numberOfEmployees = parseAmount(employeeCount);
  if (!salariesWages || !numberOfEmployees) return null;
  const impliedMonthlySalaryPerEmployee = (salariesWages / 12) / numberOfEmployees;
  if (impliedMonthlySalaryPerEmployee < 50000) {
    const formatted = Math.round(impliedMonthlySalaryPerEmployee).toLocaleString();
    return `⚠️ Salary check: implied monthly pay per employee is ${currency}${formatted} — this may appear unrealistically low to lenders. Please confirm.`;
  }
  return null;
};

const emptyProduct = () => ({ product_name: "", product_desc: "", product_price: "" });
const emptyUtilItem = () => ({ util_item: "", util_amount: "", util_purpose: "" });
const normalizeRisk = (r = {}) => ({
  risk_name: (r.risk_name || r.risk_factor || r.name || "").trim(),
  risk_likelihood: r.risk_likelihood || "Medium",
  risk_impact: r.risk_impact || "Medium",
  risk_mitigation: r.risk_mitigation || "",
});
const emptyRisk = () => normalizeRisk();
const emptyTeamMember = () => ({ team_name: "", team_role: "", team_experience: "" });

const INITIAL_DATA = {
  template_layout: "classic",
  business_name: "",
  industry: "",
  industry_sector: "",
  industry_other: "",
  legal_structure: "",
  reg_number: "",
  year_established: "",
  business_address: "",
  business_email: "",
  business_phone: "",
  business_website: "",
  employee_count: "",
  current_revenue: "",
  business_stage: "",
  currency: "NGN",
  plan_date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }),
  funding_type: "",
  funding_amount: "",
  funding_purpose: "",
  prepared_for: "",
  utilization: [emptyUtilItem()],
  products: [emptyProduct()],
  unique_value_proposition: "",
  target_market: "",
  market_size: "",
  geographic_focus: "",
  customer_profile: "",
  revenue_model: "",
  pricing_strategy: "",
  sales_channels: "",
  customer_acquisition: "",
  team: [emptyTeamMember()],
  signatoryName: "",
  signatoryTitle: "",
  financial_inputs: {
    current_annual_revenue: "",
    growth_pct: "10",
    annual_cost_of_sales: "",
    monthly_salary: "",
    monthly_mkt: "",
    monthly_admin: "",
    opening_cash: "",
    fixed_costs: "",
    direct_cost_per_transaction: "",
    sale_value_per_transaction: "",
  },
  repayment_period: "",
  monthly_repayment: "",
  interest_rate: "",
  repayment_source: "",
  collateral: "",
  risks: [emptyRisk()],
  executive_summary: "",
  mission_statement: "",
  vision_statement: "",
  business_description: "",
  industry_trends: "",
  competitive_landscape: "",
  competitive_advantage: "",
  strengths: "",
  weaknesses: "",
  opportunities: "",
  threats: "",
  funding_justification: "",
  financial_assumptions: "",
  repayment_strategy: "",
  risk_intro: "",
  closing_statement: "",
};

const normalizeFinancialInputs = (fi = {}) => {
  const base = {
    ...INITIAL_DATA.financial_inputs,
    ...fi,
    annual_cost_of_sales: fi.annual_cost_of_sales ?? "",
    direct_cost_per_transaction: fi.direct_cost_per_transaction ?? fi.var_cost_per_unit ?? "",
    sale_value_per_transaction: fi.sale_value_per_transaction ?? fi.sell_price_per_unit ?? "",
    current_cash_balance: fi.current_cash_balance ?? fi.opening_cash ?? "",
  };
  if (!base.current_annual_revenue) {
    base.current_annual_revenue = fi.current_annual_revenue ?? fi.rev_y1 ?? "";
  }
  if (!base.monthly_salary && fi.salary_y1) {
    base.monthly_salary = String(Math.round(parseAmount(fi.salary_y1) / 12) || "");
  }
  if (!base.monthly_mkt && fi.mkt_y1) {
    base.monthly_mkt = String(Math.round(parseAmount(fi.mkt_y1) / 12) || "");
  }
  if (!base.monthly_admin && fi.admin_y1) {
    base.monthly_admin = String(Math.round(parseAmount(fi.admin_y1) / 12) || "");
  }
  return base;
};

/** Wizard fields only — excludes server-computed financials attached on rerun restore. */
function pickWizardFields(source = {}) {
  const migrated = migrateSwotWizardFields(source);
  const picked = {};
  for (const key of Object.keys(INITIAL_DATA)) {
    if (migrated[key] !== undefined && migrated[key] !== null) {
      picked[key] = migrated[key];
    }
  }
  return picked;
}

function buildBusinessPlanSubmitPayload(data, overrides = {}) {
  const payload = pickWizardFields(data);
  payload.industry = resolveIndustry(payload.industry_sector, payload.industry_other);
  payload.financial_inputs = {
    ...(payload.financial_inputs || INITIAL_DATA.financial_inputs),
    funding_amount: data.funding_amount,
  };
  payload.signatory_name = data.signatoryName;
  payload.signatory_title = data.signatoryTitle;
  payload.authorized_signatory_name = data.signatoryName;
  payload.authorized_signatory_title = data.signatoryTitle;
  return { ...payload, ...overrides };
}

async function dataURIToFileAsync(dataURI, filename = "logo.png") {
  if (!dataURI || typeof dataURI !== "string") return null;
  try {
    const res = await fetch(dataURI);
    const blob = await res.blob();
    if (!blob.size) return null;
    const ext = (blob.type || "").includes("jpeg") ? "jpg" : "png";
    const name = filename.includes(".") ? filename : `${filename}.${ext}`;
    return new File([blob], name, { type: blob.type || "image/png" });
  } catch {
    return null;
  }
}

async function readApiErrorDetail(err) {
  if (!err.response) {
    return err.code === "ERR_NETWORK"
      ? "Unable to reach the server. Check your connection and try again."
      : (err.message || "Generation failed. Please try again.");
  }
  const body = err.response.data;
  if (body instanceof Blob) {
    try {
      const text = await body.text();
      const parsed = JSON.parse(text);
      const detail = parsed?.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      }
      return text || err.message;
    } catch {
      return err.message || "Generation failed. Please try again.";
    }
  }
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
  }
  return err.message || "Generation failed. Please try again.";
}

/* ─── Shared UI helpers (module-level — stable across renders) ───────── */
function FieldRow({ label, hint, children, required, error }) {
  return (
    <div className="bp-field">
      <label className="bp-label">
        {label}
        {required && <span className="bp-req">*</span>}
      </label>
      {children}
      {hint && <p className="bp-hint">{hint}</p>}
      {error && <span className="bp-error">{error}</span>}
    </div>
  );
}

function LoopRow({ children, onRemove, removable }) {
  return (
    <div className="bp-loop-row">
      {children}
      {removable && (
        <button className="bp-remove-btn" onClick={onRemove} type="button">✕</button>
      )}
    </div>
  );
}

function NarrativeFieldRow({ label, hint, value, onChange, rows = 4, placeholder = "", loading = false, error = "" }) {
  return (
    <FieldRow label={label} hint={hint} error={error}>
      <textarea
        className="bp-textarea"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {loading && <p className="bp-hint">Drafting...</p>}
    </FieldRow>
  );
}

export default function BusinessPlanWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rerunJobId = searchParams.get("rerunJobId");
  const urlTemplateLayout = searchParams.get("templateLayout");
  const urlTemplateId = searchParams.get("templateId");
  const { user, api, token } = useAuth();

  const authRequestConfig = () => {
    const headers = getAuthHeaders(token);
    if (!headers.Authorization) {
      alert("Your session has expired. Please sign in again.");
      return null;
    }
    return { headers };
  };
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rerunRestoring, setRerunRestoring] = useState(Boolean(rerunJobId));
  const [computedFin, setComputedFin] = useState(null);
  const [computedWarnings, setComputedWarnings] = useState([]);
  const [aiDraftError, setAiDraftError] = useState(false);
  const [aiDraftErrors, setAiDraftErrors] = useState({});
  const [draftAllProgress, setDraftAllProgress] = useState({ active: false, completed: 0, total: 0 });
  const [showEmptyNarrativeConfirm, setShowEmptyNarrativeConfirm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDraftPill, setShowDraftPill] = useState(false);
  const [documentGenerated, setDocumentGenerated] = useState(false);
  const [generationJobId, setGenerationJobId] = useState(null);
  const [generationStatusMessage, setGenerationStatusMessage] = useState("");
  const [generationError, setGenerationError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const pollTimerRef = useRef(null);
  const [data, setData] = useState(INITIAL_DATA);

  const draftRestoredRef = useRef(false);
  const rerunRestoreRef = useRef(false);

  const draftKey = useMemo(
    () => (user?.id ? `business_plan_${DRAFT_VERSION}_${user.id}` : null),
    [user?.id],
  );

  const currencyPrefix = useMemo(
    () => getCurrencyDisplayPrefix(data.currency),
    [data.currency],
  );

  const monthlyInstallment = useMemo(() => {
    const amount = calcMonthlyInstallment(
      parseAmount(data.funding_amount),
      parseInterestRate(data.interest_rate),
      parseRepaymentMonths(data.repayment_period),
    );
    return amount > 0 ? Math.round(amount) : 0;
  }, [data.funding_amount, data.interest_rate, data.repayment_period]);

  const formattedMonthlyInstallment = useMemo(
    () => (monthlyInstallment > 0 ? monthlyInstallment.toLocaleString("en-US") : ""),
    [monthlyInstallment],
  );

  const fileToDataURI = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const dataURIToFile = (dataURI, filename) => {
    if (!dataURI || typeof dataURI !== "string") return null;
    try {
      const [header, base64] = dataURI.split(",");
      const mime = (header.match(/data:(.+?);/) || [])[1] || "image/png";
      const bin = atob(base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new File([arr], filename, { type: mime });
    } catch {
      return null;
    }
  };

  /* ── Require template gallery selection before wizard (rerun exempt) ─ */
  useEffect(() => {
    if (rerunJobId) return;
    if (!urlTemplateLayout && !urlTemplateId) {
      toast.error("No template selected. Pick a template first.");
      navigate("/documents/business-proposal/templates?category=business_plan_funding");
      return;
    }
    const layout = (urlTemplateLayout || "").toLowerCase();
    if (BP_LAYOUT_VALUES.includes(layout)) {
      setData((d) => ({ ...d, template_layout: layout }));
      return;
    }
    if (!urlTemplateId) return;
    (async () => {
      try {
        const res = await api.get("/templates/by-type/business_plan_funding", {
          params: { category: "business_plan_funding" },
        });
        const tpl = (res.data?.templates || []).find((t) => t.id === urlTemplateId);
        if (tpl) {
          setData((d) => ({ ...d, template_layout: resolveBpLayoutFromTemplate(tpl) }));
        }
      } catch {
        /* keep default layout */
      }
    })();
  }, [rerunJobId, urlTemplateLayout, urlTemplateId, navigate, api]);

  /* ── Server rerun restore (dashboard "Reuse last document") ──────────── */
  useEffect(() => {
    if (!rerunJobId || rerunRestoreRef.current) return;
    rerunRestoreRef.current = true;
    setRerunRestoring(true);
    (async () => {
      try {
        const [rerunRes, logosRes] = await Promise.all([
          api.get(`/jobs/${rerunJobId}/rerun-data`),
          api.get(`/documents/${rerunJobId}/logos`).catch(() => ({ data: { logo: null } })),
        ]);
        const inputData = rerunRes.data?.input_data || {};
        const wizardFields = pickWizardFields(inputData);
        setData((d) => ({
          ...d,
          ...wizardFields,
          ...normalizeIndustryFields(wizardFields),
          signatoryName: wizardFields.signatoryName ?? inputData.signatory_name ?? inputData.authorized_signatory_name ?? d.signatoryName,
          signatoryTitle: wizardFields.signatoryTitle ?? inputData.signatory_title ?? inputData.authorized_signatory_title ?? d.signatoryTitle,
          business_stage: normalizeBusinessStage(wizardFields.business_stage ?? d.business_stage),
          utilization: wizardFields.utilization?.length ? wizardFields.utilization : d.utilization,
          products: wizardFields.products?.length ? wizardFields.products : d.products,
          team: wizardFields.team?.length ? wizardFields.team : d.team,
          risks: wizardFields.risks?.length ? wizardFields.risks.map(normalizeRisk) : d.risks,
          financial_inputs: normalizeFinancialInputs(wizardFields.financial_inputs),
          currency: normalizeCurrencyCode(wizardFields.currency ?? inputData.currency ?? d.currency),
        }));
        if (inputData.financials && inputData.cash_flow && inputData.break_even) {
          setComputedFin({
            financials: inputData.financials,
            cash_flow: inputData.cash_flow,
            break_even: inputData.break_even,
            monthly_installment: inputData.monthly_installment,
          });
        }
        const lf = await dataURIToFileAsync(logosRes.data?.logo, "restored-logo");
        if (lf) {
          setLogo(lf);
          setLogoPreview(URL.createObjectURL(lf));
          toast.success("Previous business plan data and logo loaded. Edit and regenerate when ready.");
        } else {
          toast.success("Previous business plan data loaded. Edit and regenerate when ready.");
        }
      } catch {
        toast.error("Could not load previous business plan data — starting fresh.");
      } finally {
        setRerunRestoring(false);
      }
    })();
  }, [rerunJobId, api]);

  /* ── Draft restore ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey) return;
    if (rerunJobId) return;
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.data) {
        setData((d) => ({
          ...d,
          ...migrateSwotWizardFields(parsed.data),
          ...normalizeIndustryFields(parsed.data),
          signatoryName: parsed.data.signatoryName ?? parsed.data.signatory_name ?? d.signatoryName,
          signatoryTitle: parsed.data.signatoryTitle ?? parsed.data.signatory_title ?? d.signatoryTitle,
          business_stage: normalizeBusinessStage(parsed.data.business_stage),
          utilization: parsed.data.utilization?.length ? parsed.data.utilization : d.utilization,
          products: parsed.data.products?.length ? parsed.data.products : d.products,
          team: parsed.data.team?.length ? parsed.data.team : d.team,
          risks: parsed.data.risks?.length
            ? parsed.data.risks.map(normalizeRisk)
            : d.risks,
          financial_inputs: normalizeFinancialInputs(parsed.data.financial_inputs),
          currency: normalizeCurrencyCode(parsed.data.currency ?? d.currency),
        }));
      }
      if (parsed?.step) setStep(parsed.step);
      if (parsed?.computedFin) setComputedFin(parsed.computedFin);
      const lf = dataURIToFile(parsed?.images?.logo, "draft-logo.png");
      if (lf) {
        setLogo(lf);
        setLogoPreview(URL.createObjectURL(lf));
      }
      setShowDraftPill(true);
      toast.success("Draft restored — pick up where you left off.");
      if (parsed.active_generation_job_id) {
        setGenerating(true);
        setGenerationJobId(parsed.active_generation_job_id);
        pollGenerationJob(parsed.active_generation_job_id);
      }
    } catch {
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    }
  }, [draftKey]);

  /* ── Autosave ──────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey || documentGenerated || rerunJobId) return;
    const id = setInterval(async () => {
      try {
        let logoUri = null;
        if (logo && logo.size <= 1024 * 1024) logoUri = await fileToDataURI(logo);
        const payload = JSON.stringify({
          data,
          step,
          computedFin,
          images: { logo: logoUri },
          saved_at: new Date().toISOString(),
        });
        if (payload.length > 4 * 1024 * 1024) {
          localStorage.setItem(draftKey, JSON.stringify({
            data, step, computedFin, images: {}, saved_at: new Date().toISOString(),
          }));
        } else {
          localStorage.setItem(draftKey, payload);
        }
      } catch { /* quota */ }
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [draftKey, data, step, logo, computedFin, documentGenerated, rerunJobId]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setShowDraftPill(false);
    toast.info("Draft cleared.");
  };
  const dismissDraftPill = () => setShowDraftPill(false);

  const stopGenerationPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => stopGenerationPolling(), []);

  const persistActiveGenerationJob = (jobId) => {
    if (!draftKey || !jobId) return;
    try {
      const raw = localStorage.getItem(draftKey);
      const parsed = raw ? JSON.parse(raw) : { data, step };
      localStorage.setItem(draftKey, JSON.stringify({
        ...parsed,
        active_generation_job_id: jobId,
      }));
    } catch { /* ignore */ }
  };

  const clearActiveGenerationJob = () => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed.active_generation_job_id) return;
      delete parsed.active_generation_job_id;
      localStorage.setItem(draftKey, JSON.stringify(parsed));
    } catch { /* ignore */ }
  };

  const pollGenerationJob = (jobId) => {
    stopGenerationPolling();
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/documents/business-plan/jobs/${jobId}`, {
          timeout: GENERATION_POLL_TIMEOUT_MS,
        });
        const { status, message, download_ready, error_message: jobError } = res.data;
        setGenerationStatusMessage(message || "Preparing your business plan…");

        if (download_ready && status === "completed") {
          setGenerationJobId(jobId);
          setDocumentGenerated(true);
          setGenerating(false);
          setGenerationError(null);
          clearActiveGenerationJob();
          if (draftKey) {
            try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
            setShowDraftPill(false);
          }
          if (window.refreshUser) window.refreshUser();
          return;
        }
        if (status === "failed") {
          setGenerationError(jobError || "Generation failed. Please try again.");
          setGenerating(false);
          clearActiveGenerationJob();
          return;
        }
      } catch {
        /* transient poll failure — keep trying */
      }

      if (attempts >= GENERATION_MAX_POLL_ATTEMPTS) {
        setGenerationError("Generation timed out. Please try again.");
        setGenerating(false);
        clearActiveGenerationJob();
        return;
      }

      pollTimerRef.current = setTimeout(poll, GENERATION_POLL_INTERVAL_MS);
    };

    poll();
  };

  const handlePdfDownload = async () => {
    if (!generationJobId || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/documents/business-plan/jobs/${generationJobId}/download`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = res.data;
      const filename = res.headers["content-disposition"]?.match(/filename="?([^"]+)"?/)?.[1]
        || `${data.business_name.replace(/\s+/g, "_")}_Business_Plan.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = await readApiErrorDetail(err);
      alert(msg);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const setFinField = (key, val) => setData((d) => ({
    ...d,
    financial_inputs: { ...d.financial_inputs, [key]: val },
  }));

  useEffect(() => {
    if (!data.current_revenue || data.financial_inputs.current_annual_revenue) return;
    setData((d) => ({
      ...d,
      financial_inputs: {
        ...d.financial_inputs,
        current_annual_revenue: d.current_revenue,
      },
    }));
  }, [data.current_revenue, data.financial_inputs.current_annual_revenue]);

  const updateListItem = (listKey, idx, field, val) => {
    setData((d) => {
      const arr = [...d[listKey]];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...d, [listKey]: arr };
    });
  };

  const addListItem = (listKey, empty) => {
    setData((d) => ({ ...d, [listKey]: [...d[listKey], empty()] }));
  };

  const removeListItem = (listKey, idx) => {
    setData((d) => ({ ...d, [listKey]: d[listKey].filter((_, i) => i !== idx) }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const resolvedIndustry = resolveIndustry(data.industry_sector, data.industry_other);

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!data.business_name.trim()) e.business_name = "Required";
      if (!data.industry_sector) {
        e.industry = "Required";
      } else if (data.industry_sector === INDUSTRY_OTHER && !data.industry_other.trim()) {
        e.industry = "Please specify your industry";
      }
      if (!data.business_stage) e.business_stage = "Required";
      if (!data.currency) e.currency = "Required";
    }
    if (step === 2) {
      if (!data.funding_type.trim()) e.funding_type = "Required";
      if (!data.funding_amount.trim()) e.funding_amount = "Required";
      if (!data.funding_purpose) e.funding_purpose = "Required";
    }
    if (step === 7) {
      if (!data.financial_inputs.current_annual_revenue && !data.current_revenue) {
        e.current_annual_revenue = "Required";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep((s) => Math.min(s + 1, WIZARD_STEP_COUNT)); };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const handleComputeFinancials = async () => {
    setLoading(true);
    try {
      const authConfig = authRequestConfig();
      if (!authConfig) return;
      const res = await api.post(
        "/documents/business-plan/compute-financials",
        {
          ...data.financial_inputs,
          current_cash_balance:
            data.financial_inputs.current_cash_balance || data.financial_inputs.opening_cash,
          current_revenue: data.current_revenue,
          funding_amount: data.funding_amount,
          repayment_period: data.repayment_period,
          interest_rate: data.interest_rate,
          monthly_repayment: formattedMonthlyInstallment || undefined,
          products: data.products,
        },
        authConfig,
      );
      setComputedFin(res.data);
      const netProfitY1 = parseFloat((res.data.financials?.net_profit_y1 || "").replace(/,/g, ""));
      if (!isNaN(netProfitY1) && netProfitY1 < 0) {
        setComputedWarnings(["⚠ Your Year 1 net profit is negative. Review your cost structure before submitting to a lender."]);
      } else {
        setComputedWarnings([]);
      }
    } catch {
      alert("Could not compute financials. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const aiPayload = () => {
    const projected = projectRevenueYears(data.financial_inputs, data.current_revenue);
    return {
      business_name: data.business_name,
      industry: resolvedIndustry,
      business_stage: data.business_stage,
      business_description: data.business_description,
      currency: data.currency,
      funding_amount: data.funding_amount,
      funding_purpose: data.funding_purpose,
      funding_type: data.funding_type,
      current_revenue: data.current_revenue,
      current_annual_revenue: data.financial_inputs.current_annual_revenue || data.current_revenue,
      employee_count: data.employee_count,
      repayment_period: data.repayment_period,
      monthly_repayment: formattedMonthlyInstallment,
      monthly_installment: formattedMonthlyInstallment,
      interest_rate: data.interest_rate,
      annual_cost_of_sales: data.financial_inputs.annual_cost_of_sales,
      repayment_source: data.repayment_source,
      collateral: data.collateral,
      target_market: data.target_market,
      revenue_model: data.revenue_model,
      utilization: data.utilization,
      signatory_name: data.signatoryName,
      signatory_title: data.signatoryTitle,
      authorized_signatory_name: data.signatoryName,
      authorized_signatory_title: data.signatoryTitle,
      rev_y1: projected.y1 || undefined,
      rev_y2: projected.y2 || undefined,
      rev_y3: projected.y3 || undefined,
      growth_pct: data.financial_inputs.growth_pct,

      // Market & competitive context
      products: data.products,
      unique_value_proposition: data.unique_value_proposition,
      geographic_focus: data.geographic_focus,
      customer_profile: data.customer_profile,
      market_size: data.market_size,
      competitors: data.competitive_landscape || data.competitors,
      pricing_strategy: data.pricing_strategy,
      sales_channels: data.sales_channels,

      // Full opex breakdown (for financial assumptions prompt)
      monthly_salary: data.financial_inputs?.monthly_salary,
      monthly_mkt: data.financial_inputs?.monthly_mkt,
      monthly_admin: data.financial_inputs?.monthly_admin,

      // Break-even
      break_even_revenue: data.break_even?.revenue || data.financial_inputs?.break_even_revenue,

      // Team & risk (useful for completeness)
      team: data.team,
      risks: data.risks,
    };
  };

  const draftSectionWithTimeout = async (section, authConfig, signal) => {
    const res = await api.post(
      "/documents/business-plan/draft-section",
      { ...aiPayload(), section },
      { ...authConfig, timeout: 60000, signal },
    );
    return res.data;
  };

  const handleDraftAll = async () => {
    const authConfig = authRequestConfig();
    if (!authConfig) return;

    const total = DRAFT_ALL_JOBS.length;
    setAiDraftError(false);
    setAiDraftErrors({});
    setDraftAllProgress({ active: true, completed: 0, total });
    setAiLoading((prev) => ({ ...prev, all: true }));

    const loadingFlags = {};
    DRAFT_ALL_JOBS.forEach((job) => {
      job.fields.forEach((field) => {
        loadingFlags[`draft_${field}`] = true;
      });
    });
    setAiLoading((prev) => ({ ...prev, ...loadingFlags }));

    let completed = 0;
    const updateProgress = () => {
      completed += 1;
      setDraftAllProgress({ active: true, completed, total });
    };

    const jobs = DRAFT_ALL_JOBS.map(async (job) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      try {
        const result = await draftSectionWithTimeout(job.section, authConfig, controller.signal);
        const mapped = job.section === "swot" ? mapSwotDraftToState(result) : result;
        setData((d) => ({ ...d, ...mapped }));
        setAiDraftErrors((prev) => {
          const next = { ...prev };
          job.fields.forEach((field) => {
            delete next[field];
          });
          return next;
        });
      } catch {
        setAiDraftErrors((prev) => {
          const next = { ...prev };
          job.fields.forEach((field) => {
            next[field] = "Draft failed — please try again individually";
          });
          return next;
        });
      } finally {
        clearTimeout(timeoutId);
        setAiLoading((prev) => {
          const next = { ...prev };
          job.fields.forEach((field) => {
            delete next[`draft_${field}`];
          });
          return next;
        });
        updateProgress();
      }
    });

    await Promise.allSettled(jobs);

    setAiLoading((prev) => ({ ...prev, all: false }));
    setDraftAllProgress({ active: false, completed: total, total });
  };

  const handleUniqueValuePropositionDraft = async () => {
    setAiLoading((prev) => ({ ...prev, uvp: true }));
    const payload = {
      section: "unique_value_proposition",
      business_name: data.business_name,
      industry: resolvedIndustry,
      products: data.products,
      target_market: data.target_market,
    };
    try {
      const authConfig = authRequestConfig();
      if (!authConfig) return;
      const res = await api.post("/documents/business-plan/draft-section", payload, authConfig);
      setField("unique_value_proposition", res.data.unique_value_proposition || "");
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      console.error("[uvp-ai]", status, body);
      const detail = body?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (detail || err.message || "Suggestion failed. Please try again.");
      alert(msg);
    } finally {
      setAiLoading((prev) => ({ ...prev, uvp: false }));
    }
  };

  const handleRiskMitigationSuggest = async (idx) => {
    const risk = data.risks[idx];
    const riskName = (risk?.risk_name || "").trim();
    if (!riskName) return;
    setAiLoading((prev) => ({ ...prev, [`risk_${idx}`]: true }));
    const payload = {
      section: "risk_mitigation",
      risk_name: riskName,
      risk_likelihood: risk?.risk_likelihood || "Medium",
      risk_impact: risk?.risk_impact || "Medium",
      industry: resolvedIndustry,
      business_name: data.business_name,
      funding_type: data.funding_type,
    };
    console.log("[risk-mitigation-ai] request", payload);
    try {
      const authConfig = authRequestConfig();
      if (!authConfig) return;
      const res = await api.post("/documents/business-plan/draft-section", payload, authConfig);
      updateListItem("risks", idx, "risk_mitigation", res.data.risk_mitigation || "");
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      console.error("[risk-mitigation-ai]", status, body);
      const detail = body?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (detail || err.message || "Suggestion failed. Please try again.");
      alert(msg);
    } finally {
      setAiLoading((prev) => ({ ...prev, [`risk_${idx}`]: false }));
    }
  };

  const hasEmptyNarratives = () => NARRATIVE_KEYS.some((k) => !data[k]?.trim());

  const doGenerate = async () => {
    if (rerunRestoring) return;
    setShowEmptyNarrativeConfirm(false);
    setGenerating(true);
    setGenerationError(null);
    setGenerationStatusMessage("Preparing your business plan…");
    setDocumentGenerated(false);
    setGenerationJobId(null);
    stopGenerationPolling();
    try {
      const fd = new FormData();
      const payload = buildBusinessPlanSubmitPayload(data, {
        monthly_repayment: formattedMonthlyInstallment,
        monthly_installment: formattedMonthlyInstallment,
      });
      console.log("[business-plan:generate:swot]", {
        fieldNames: SWOT_FIELD_NAMES,
        lengths: Object.fromEntries(
          SWOT_FIELD_NAMES.map((key) => [key, (payload[key] || "").length]),
        ),
        preview: Object.fromEntries(
          SWOT_FIELD_NAMES.map((key) => [key, String(payload[key] || "").slice(0, 80)]),
        ),
      });
      fd.append("data", JSON.stringify(payload));
      if (logo && logo.size > 0) {
        fd.append("logo", logo, logo.name || "logo.png");
      }

      const res = await api.post("/documents/business-plan/jobs", fd, {
        timeout: GENERATION_JOB_CREATE_TIMEOUT_MS,
      });

      const jobId = res.data?.job_id;
      if (!jobId) {
        throw new Error("Generation job was not created. Please try again.");
      }

      setGenerationJobId(jobId);
      persistActiveGenerationJob(jobId);
      setGenerationStatusMessage(res.data?.message || "Preparing your business plan…");
      pollGenerationJob(jobId);
    } catch (err) {
      const msg = await readApiErrorDetail(err);
      console.error("[business-plan-generate]", err.response?.status, err.response?.data || err.message);
      setGenerationError(msg);
      setGenerating(false);
      stopGenerationPolling();
    }
  };

  const handleGenerate = () => {
    setShowReviewModal(true);
  };

  const handleReviewConfirm = () => {
    setShowReviewModal(false);
    if (hasEmptyNarratives()) {
      setShowEmptyNarrativeConfirm(true);
      return;
    }
    doGenerate();
  };

  const renderStep1 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Business Information</h2>
      <p className="bp-step-desc">Tell us about your business. This information will appear throughout the plan.</p>

      <div className="bp-row">
        <FieldRow label="Business Name" required error={errors.business_name}>
          <input
            className={`bp-input ${errors.business_name ? "bp-input--error" : ""}`}
            type="text"
            value={data.business_name}
            onChange={(e) => setField("business_name", e.target.value)}
            placeholder="e.g. Apex Ventures Ltd"
          />
        </FieldRow>
        <FieldRow label="Industry / Sector" required error={errors.industry}>
          <IndustrySelector
            sector={data.industry_sector}
            other={data.industry_other}
            error={errors.industry}
            onSectorChange={(sector) => setData((d) => ({
              ...d,
              industry_sector: sector,
              industry_other: sector === INDUSTRY_OTHER ? d.industry_other : "",
            }))}
            onOtherChange={(other) => setField("industry_other", other)}
          />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Legal Structure">
          <select className="bp-input" value={data.legal_structure} onChange={(e) => setField("legal_structure", e.target.value)}>
            <option value="">Select...</option>
            {LEGAL_STRUCTURES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Registration Number">
          <input className="bp-input" type="text" value={data.reg_number} onChange={(e) => setField("reg_number", e.target.value)} placeholder="e.g. RC 1234567" />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Year Established">
          <input className="bp-input" type="number" value={data.year_established} onChange={(e) => setField("year_established", e.target.value)} placeholder="e.g. 2019" />
        </FieldRow>
        <FieldRow label="Number of Employees">
          <input className="bp-input" type="number" value={data.employee_count} onChange={(e) => setField("employee_count", e.target.value)} placeholder="e.g. 12" />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Business Stage" required error={errors.business_stage}>
          <select className={`bp-input ${errors.business_stage ? "bp-input--error" : ""}`} value={data.business_stage} onChange={(e) => setField("business_stage", e.target.value)}>
            <option value="">Select...</option>
            {BUSINESS_STAGES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Currency" required error={errors.currency}>
          <CurrencySelector
            value={data.currency}
            onChange={(v) => setField("currency", v)}
            className={errors.currency ? "bp-input bp-input--error" : "bp-input"}
          />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Current Annual Revenue">
          <input className="bp-input" type="text" value={data.current_revenue} onChange={(e) => setField("current_revenue", e.target.value)} placeholder="e.g. 5,000,000" />
        </FieldRow>
        <FieldRow label="Business Email">
          <input className="bp-input" type="email" value={data.business_email} onChange={(e) => setField("business_email", e.target.value)} placeholder="info@mybusiness.com" />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Phone Number">
          <input className="bp-input" type="text" value={data.business_phone} onChange={(e) => setField("business_phone", e.target.value)} placeholder="+234 800 000 0000" />
        </FieldRow>
        <FieldRow label="Website">
          <input className="bp-input" type="text" value={data.business_website} onChange={(e) => setField("business_website", e.target.value)} placeholder="www.mybusiness.com" />
        </FieldRow>
      </div>
      <FieldRow label="Business Address">
        <input className="bp-input" type="text" value={data.business_address} onChange={(e) => setField("business_address", e.target.value)} placeholder="12 Example Street, Lagos, Nigeria" />
      </FieldRow>

      <div className="bp-row bp-row--top">
        <div className="bp-field">
          <label className="bp-label">Template Style</label>
          <div className="bp-template-cards">
            {TEMPLATES.map((t) => (
              <div
                key={t.value}
                className={`bp-template-card ${data.template_layout === t.value ? "bp-template-card--active" : ""}`}
                onClick={() => setField("template_layout", t.value)}
                onKeyDown={(e) => e.key === "Enter" && setField("template_layout", t.value)}
                role="button"
                tabIndex={0}
              >
                <div className="bp-template-name">{t.label}</div>
                <div className="bp-template-desc">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bp-field">
          <label className="bp-label">Business Logo <span className="bp-optional">(optional)</span></label>
          <div className="bp-logo-upload" onClick={() => document.getElementById("logo-input").click()} onKeyDown={(e) => e.key === "Enter" && document.getElementById("logo-input").click()} role="button" tabIndex={0}>
            {logoPreview
              ? <img src={logoPreview} alt="Logo preview" className="bp-logo-preview" />
              : <div className="bp-logo-placeholder"><span>📁</span><p>Click to upload logo</p><small>PNG or JPG recommended</small></div>}
          </div>
          <input id="logo-input" type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoChange} />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Funding Request</h2>
      <p className="bp-step-desc">Define your funding requirement. This powers the executive summary, funding section, and financial projections.</p>

      <div className="bp-row">
        <FieldRow label="Funding Type" required error={errors.funding_type}>
          <input className={`bp-input ${errors.funding_type ? "bp-input--error" : ""}`} type="text" value={data.funding_type} onChange={(e) => setField("funding_type", e.target.value)} placeholder="e.g. Bank Loan, SME Grant, Investor Funding" />
        </FieldRow>
        <FieldRow label="Funding Amount" required error={errors.funding_amount}>
          <input className={`bp-input ${errors.funding_amount ? "bp-input--error" : ""}`} type="text" value={data.funding_amount} onChange={(e) => setField("funding_amount", e.target.value)} placeholder="e.g. 10,000,000" />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Primary Purpose" required error={errors.funding_purpose}>
          <select className={`bp-input ${errors.funding_purpose ? "bp-input--error" : ""}`} value={data.funding_purpose} onChange={(e) => setField("funding_purpose", e.target.value)}>
            <option value="">Select...</option>
            {FUNDING_PURPOSES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Repayment Period">
          <input className="bp-input" type="text" value={data.repayment_period} onChange={(e) => setField("repayment_period", e.target.value)} placeholder="e.g. 24 months, 3 years" />
        </FieldRow>
      </div>
      <FieldRow
        label="Prepared For (optional)"
        hint="Lender or bank name — shown on the cover page as 'Prepared for: …'. Leave blank to omit."
      >
        <input className="bp-input" type="text" value={data.prepared_for} onChange={(e) => setField("prepared_for", e.target.value)} placeholder="e.g. First Bank of Nigeria" />
      </FieldRow>

      <div className="bp-section-divider">Funding Utilization Breakdown</div>
      <p className="bp-hint">Break down exactly how the funds will be used. Total should equal funding amount.</p>

      {data.utilization.map((item, idx) => (
        <LoopRow key={idx} removable={data.utilization.length > 1} onRemove={() => removeListItem("utilization", idx)}>
          <div className="bp-loop-fields">
            <FieldRow label="Item / Category">
              <input className="bp-input" type="text" value={item.util_item} onChange={(e) => updateListItem("utilization", idx, "util_item", e.target.value)} placeholder="e.g. Equipment Purchase" />
            </FieldRow>
            <FieldRow label={`Amount (${data.currency})`}>
              <input className="bp-input" type="text" value={item.util_amount} onChange={(e) => updateListItem("utilization", idx, "util_amount", e.target.value)} placeholder="e.g. 5,000,000" />
            </FieldRow>
            <FieldRow label="Purpose / Description">
              <input className="bp-input" type="text" value={item.util_purpose} onChange={(e) => updateListItem("utilization", idx, "util_purpose", e.target.value)} placeholder="e.g. Purchase of industrial machinery" />
            </FieldRow>
          </div>
        </LoopRow>
      ))}
      <button className="bp-add-btn" onClick={() => addListItem("utilization", emptyUtilItem)} type="button">+ Add Item</button>
    </div>
  );

  const renderStep3 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Products & Services</h2>
      <p className="bp-step-desc">List what your business offers. These will appear in the Products & Services table.</p>

      {data.products.map((item, idx) => (
        <LoopRow key={idx} removable={data.products.length > 1} onRemove={() => removeListItem("products", idx)}>
          <div className="bp-loop-fields bp-loop-fields--3">
            <FieldRow label="Product / Service Name">
              <input className="bp-input" type="text" value={item.product_name} onChange={(e) => updateListItem("products", idx, "product_name", e.target.value)} placeholder="e.g. Commercial Cleaning Service" />
            </FieldRow>
            <FieldRow label="Description">
              <input className="bp-input" type="text" value={item.product_desc} onChange={(e) => updateListItem("products", idx, "product_desc", e.target.value)} placeholder="Brief description" />
            </FieldRow>
            <FieldRow label="Price / Rate">
              <input className="bp-input" type="text" value={item.product_price} onChange={(e) => updateListItem("products", idx, "product_price", e.target.value)} placeholder={`e.g. ${currencyPrefix}85,000/unit`} />
            </FieldRow>
          </div>
        </LoopRow>
      ))}
      <button className="bp-add-btn" onClick={() => addListItem("products", emptyProduct)} type="button">+ Add Product / Service</button>

      <div className="bp-field">
        <div className="bp-label-row">
          <label className="bp-label">Unique Value Proposition</label>
          <button
            className="bp-ai-btn"
            type="button"
            disabled={aiLoading.uvp || !data.business_name.trim()}
            onClick={handleUniqueValuePropositionDraft}
          >
            {aiLoading.uvp ? "✦ Drafting..." : "Get Suggestions"}
          </button>
        </div>
        <textarea
          className="bp-textarea"
          rows={3}
          value={data.unique_value_proposition}
          placeholder="What makes your business stand out from competitors?"
          onChange={(e) => setField("unique_value_proposition", e.target.value)}
        />
        <p className="bp-hint">What makes your business stand out from competitors?</p>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Market & Competition</h2>
      <p className="bp-step-desc">Define your market. Suggestions will generate industry trends and competitive analysis based on your inputs.</p>

      <div className="bp-row">
        <FieldRow label="Target Market">
          <input className="bp-input" type="text" value={data.target_market} onChange={(e) => setField("target_market", e.target.value)} placeholder="e.g. SMEs in Lagos, retail consumers aged 25–45" />
        </FieldRow>
        <FieldRow
          label="Market Size"
          hint="Estimate the total annual value of your target market. E.g. ₦500 billion, $2 million"
        >
          <input className="bp-input" type="text" value={data.market_size} onChange={(e) => setField("market_size", e.target.value)} placeholder={`e.g. ${currencyPrefix}500 billion annually`} />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Geographic Focus">
          <input className="bp-input" type="text" value={data.geographic_focus} onChange={(e) => setField("geographic_focus", e.target.value)} placeholder="e.g. Lagos, Southwest Nigeria" />
        </FieldRow>
        <FieldRow label="Customer Profile">
          <input className="bp-input" type="text" value={data.customer_profile} onChange={(e) => setField("customer_profile", e.target.value)} placeholder="e.g. Mid-size businesses with 10–50 employees" />
        </FieldRow>
      </div>

      <div className="bp-ai-suggest-bar">
        <p className="bp-hint">Suggestions will generate Industry Trends, Competitive Landscape, and Competitive Advantage in Step 10 based on these inputs.</p>
      </div>
    </div>
  );

  const renderStep5 = () => {
    const placeholders = INDUSTRY_PLACEHOLDERS[resolvedIndustry] || GENERIC_PLACEHOLDERS;
    return (
      <div className="bp-step">
        <h2 className="bp-step-title">Business Model & Revenue Strategy</h2>
        <p className="bp-step-desc">Explain how your business generates revenue and acquires customers.</p>

        <NarrativeFieldRow label="How the Business Generates Revenue" value={data.revenue_model} onChange={(v) => setField("revenue_model", v)} rows={3} placeholder={placeholders.revenue_model} />
        <NarrativeFieldRow label="Pricing Strategy" value={data.pricing_strategy} onChange={(v) => setField("pricing_strategy", v)} rows={3} placeholder={placeholders.pricing_strategy} />
        <NarrativeFieldRow label="Sales & Distribution Channels" value={data.sales_channels} onChange={(v) => setField("sales_channels", v)} rows={3} placeholder={placeholders.sales_channels} />
        <NarrativeFieldRow label="Customer Acquisition Strategy" value={data.customer_acquisition} onChange={(v) => setField("customer_acquisition", v)} rows={3} placeholder={placeholders.customer_acquisition} />
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Management Team</h2>
      <p className="bp-step-desc">List the key people running the business. Lenders want to see experienced leadership.</p>

      {data.team.map((member, idx) => (
        <LoopRow key={idx} removable={data.team.length > 1} onRemove={() => removeListItem("team", idx)}>
          <div className="bp-loop-fields bp-loop-fields--3">
            <FieldRow label="Full Name">
              <input className="bp-input" type="text" value={member.team_name} onChange={(e) => updateListItem("team", idx, "team_name", e.target.value)} placeholder="e.g. Amara Johnson" />
            </FieldRow>
            <FieldRow label="Role / Title">
              <input className="bp-input" type="text" value={member.team_role} onChange={(e) => updateListItem("team", idx, "team_role", e.target.value)} placeholder="e.g. Chief Executive Officer" />
            </FieldRow>
            <FieldRow label="Experience & Qualifications">
              <input className="bp-input" type="text" value={member.team_experience} onChange={(e) => updateListItem("team", idx, "team_experience", e.target.value)} placeholder="e.g. 10 years in logistics, MBA from University of Lagos" />
            </FieldRow>
          </div>
        </LoopRow>
      ))}
      <button className="bp-add-btn" onClick={() => addListItem("team", emptyTeamMember)} type="button">+ Add Team Member</button>

      <div className="bp-section-divider">Signatory Details</div>
      <div className="bp-row">
        <FieldRow label="Authorised Signatory Name">
          <input className="bp-input" type="text" value={data.signatoryName} onChange={(e) => setField("signatoryName", e.target.value)} placeholder="e.g. Amara Johnson" />
        </FieldRow>
        <FieldRow label="Signatory Title">
          <input className="bp-input" type="text" value={data.signatoryTitle} onChange={(e) => setField("signatoryTitle", e.target.value)} placeholder="e.g. Managing Director" />
        </FieldRow>
      </div>
    </div>
  );

  const renderStep7 = () => {
    const revenueWarnings = getRevenueWarnings(data.financial_inputs, data.current_revenue);
    const salaryPerEmployeeWarning = getSalaryPerEmployeeWarning(
      data.financial_inputs,
      data.employee_count,
      currencyPrefix,
    );
    const projected = projectRevenueYears(data.financial_inputs, data.current_revenue);
    const annualSalaryDisplay = (() => {
      const monthly = parseAmount(data.financial_inputs.monthly_salary);
      return monthly > 0 ? String(Math.round(monthly * 12)) : data.financial_inputs.monthly_salary;
    })();
    return (
    <div className="bp-step">
      <h2 className="bp-step-title">Financial Projections</h2>
      <p className="bp-step-desc">Enter a few simple figures below. Year-by-year revenue, costs, profit, cash flow, and break-even are calculated automatically.</p>

      <div className="bp-fin-section-title">Revenue</div>
      <div className="bp-fin-grid bp-fin-grid--2">
        <div className="bp-fin-cell">
          <label className="bp-fin-label">Current Annual Revenue <span className="bp-req">*</span></label>
          <div className="bp-fin-input-wrap">
            <span className="bp-fin-prefix">{currencyPrefix}</span>
            <input
              className={`bp-fin-input ${errors.current_annual_revenue ? "bp-input--error" : ""}`}
              type="text"
              value={data.financial_inputs.current_annual_revenue}
              onChange={(e) => setFinField("current_annual_revenue", e.target.value)}
              placeholder="e.g. 5,000,000"
            />
          </div>
          <small className="bp-hint">Your total sales income for the current year before any deductions. Pre-filled from Step 1 if entered there.</small>
        </div>
        <div className="bp-fin-cell">
          <label className="bp-fin-label">Expected Annual Growth %</label>
          <input
            className="bp-fin-input"
            type="number"
            min="0"
            value={data.financial_inputs.growth_pct}
            onChange={(e) => setFinField("growth_pct", e.target.value)}
            placeholder="e.g. 10"
          />
          <small className="bp-hint">Enter a realistic annual growth rate. 10–25% is typical for established businesses; above 50% may raise questions with lenders.</small>
        </div>
      </div>

      {(projected.y1 > 0 || projected.y2 > 0 || projected.y3 > 0) && (
        <div className="bp-fin-grid bp-fin-grid--4" style={{ marginTop: "0.75rem" }}>
          <div className="bp-fin-cell bp-fin-header" />
          <div className="bp-fin-cell bp-fin-header">Year 1</div>
          <div className="bp-fin-cell bp-fin-header">Year 2</div>
          <div className="bp-fin-cell bp-fin-header">Year 3</div>
          <div className="bp-fin-cell bp-fin-row-label">Projected Revenue</div>
          <div className="bp-fin-cell">{currencyPrefix}{projected.y1.toLocaleString()}</div>
          <div className="bp-fin-cell">{currencyPrefix}{projected.y2.toLocaleString()}</div>
          <div className="bp-fin-cell">{currencyPrefix}{projected.y3.toLocaleString()}</div>
        </div>
      )}

      {revenueWarnings.length > 0 && (
        <div style={{ marginTop: "0.5rem" }}>
          {revenueWarnings.map((w) => (
            <p key={w} className="bp-warning">{w}</p>
          ))}
        </div>
      )}

      {computedWarnings.map((w, i) => (
        <div key={i} className="bp-warning">{w}</div>
      ))}

      <div className="bp-fin-section-title">Cost of Sales</div>
      <div className="bp-fin-grid bp-fin-grid--1">
        <div className="bp-fin-cell">
          <label className="bp-fin-label">Estimated Annual Cost of Sales</label>
          <div className="bp-fin-input-wrap">
            <span className="bp-fin-prefix">{currencyPrefix}</span>
            <input
              className="bp-fin-input"
              type="text"
              value={data.financial_inputs.annual_cost_of_sales}
              onChange={(e) => setFinField("annual_cost_of_sales", e.target.value)}
              placeholder="e.g. 4,500,000"
            />
          </div>
          <small className="bp-hint">The direct cost of producing or delivering what you sell — raw materials, stock purchases, packaging, and direct labour. Do not include rent or salaries here.</small>
        </div>
      </div>

      <div className="bp-fin-section-title">Operating Expenses</div>
      <p className="bp-step-desc" style={{ marginTop: "-0.25rem" }}>Enter monthly amounts for marketing and admin — annual payroll and Year 2–3 projections are calculated automatically.</p>
      <div className="bp-fin-grid bp-fin-grid--2">
        <div className="bp-fin-cell">
          <label className="bp-fin-label">Annual Salaries & Wages (Total for all staff)</label>
          <div className="bp-fin-input-wrap">
            <span className="bp-fin-prefix">{currencyPrefix}</span>
            <input
              className="bp-fin-input"
              type="number"
              min="0"
              value={annualSalaryDisplay}
              onChange={(e) => {
                const annual = parseAmount(e.target.value);
                setFinField(
                  "monthly_salary",
                  annual > 0 ? String(Math.round(annual / 12)) : e.target.value,
                );
              }}
              placeholder="e.g. 6000000"
            />
          </div>
          <small className="bp-hint">Total gross salaries and wages for all staff for the full year, including yourself if you draw a salary.</small>
          {salaryPerEmployeeWarning && (
            <p className="bp-warning">{salaryPerEmployeeWarning}</p>
          )}
        </div>
        {[
          {
            label: "Monthly Marketing & Advertising Spend",
            k: "monthly_mkt",
            hint: "All spending on ads, social media promotion, print materials, and sales commissions paid to agents or middlemen.",
          },
          {
            label: "Monthly Admin & Other Costs",
            k: "monthly_admin",
            hint: "Office rent, utilities, internet, insurance, subscriptions, and any other overhead not related to production or sales.",
          },
        ].map(({ label, k, hint }) => (
          <div key={k} className="bp-fin-cell">
            <label className="bp-fin-label">{label}</label>
            <div className="bp-fin-input-wrap">
              <span className="bp-fin-prefix">{currencyPrefix}</span>
              <input
                className="bp-fin-input"
                type="number"
                min="0"
                value={data.financial_inputs[k]}
                onChange={(e) => setFinField(k, e.target.value)}
              />
            </div>
            <small className="bp-hint">{hint}</small>
          </div>
        ))}
      </div>

      <div className="bp-fin-section-title">Opening Cash</div>
      <div className="bp-fin-grid bp-fin-grid--1">
        <div className="bp-fin-cell">
          <label className="bp-fin-label">Opening Cash Balance (Year 1)</label>
          <div className="bp-fin-input-wrap">
            <span className="bp-fin-prefix">{currencyPrefix}</span>
            <input
              className="bp-fin-input"
              type="number"
              min="0"
              value={data.financial_inputs.opening_cash}
              onChange={(e) => setFinField("opening_cash", e.target.value)}
            />
          </div>
          <small className="bp-hint">Your available cash at the start of Year 1 — include savings, existing reserves, or the funding amount if it will be in your account at launch.</small>
        </div>
      </div>
      <p className="bp-hint" style={{ marginTop: "0.5rem" }}>
        Break-even analysis is calculated automatically from your revenue, cost of sales, and operating expenses (fixed costs exclude marketing). Average selling price is taken from your product prices in Step 3 when available.
      </p>

      <button className="bp-compute-btn" onClick={handleComputeFinancials} disabled={loading} type="button">
        {loading ? "Computing..." : "⚡ Compute All Financials"}
      </button>

      {computedFin && (
        <div className="bp-computed-preview">
          <div className="bp-computed-title">✅ Computed Financial Summary</div>
          <div className="bp-fin-grid bp-fin-grid--4 bp-computed-grid">
            <div className="bp-fin-cell bp-fin-header" />
            <div className="bp-fin-cell bp-fin-header">Year 1</div>
            <div className="bp-fin-cell bp-fin-header">Year 2</div>
            <div className="bp-fin-cell bp-fin-header">Year 3</div>
            {[
              ["Total Revenue", "rev"],
              ["Gross Profit", "gross_profit"],
              ["Net Profit (before repayment)", "ebitda"],
              ["Net Profit (after repayment)", "net_profit"],
            ].map(([label, k]) => (
              <React.Fragment key={k}>
                <div className="bp-fin-cell bp-fin-row-label">{label}</div>
                <div className="bp-fin-cell">{currencyPrefix}{computedFin.financials[`${k}_y1`]}</div>
                <div className="bp-fin-cell">{currencyPrefix}{computedFin.financials[`${k}_y2`]}</div>
                <div className="bp-fin-cell">{currencyPrefix}{computedFin.financials[`${k}_y3`]}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="bp-breakeven-row">
            <span>Fixed Monthly Costs: <strong>{currencyPrefix}{computedFin.break_even.fixed_costs}</strong></span>
            <span>Variable Cost / Unit: <strong>{currencyPrefix}{computedFin.break_even.var_cost}</strong></span>
            <span>Avg. Selling Price / Unit: <strong>{currencyPrefix}{computedFin.break_even.sell_price}</strong></span>
            <span>Break-Even: <strong>{computedFin.break_even.units} transactions/month</strong></span>
            <span>Break-Even Revenue: <strong>{currencyPrefix}{computedFin.break_even.revenue}/month</strong></span>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderStep8 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Repayment & Collateral</h2>
      <p className="bp-step-desc">Lenders want a clear repayment plan. Suggestions will generate a repayment narrative in Step 10.</p>

      <div className="bp-row">
        <FieldRow label="Funding Amount" hint="From Step 2 — used to calculate your estimated monthly installment.">
          <input className="bp-input bp-input--readonly" type="text" readOnly value={data.funding_amount} placeholder="Enter funding amount in Step 2" />
        </FieldRow>
        <FieldRow label="Repayment Period">
          <input className="bp-input" type="text" value={data.repayment_period} onChange={(e) => setField("repayment_period", e.target.value)} placeholder="e.g. 24 months" />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Interest Rate (%)">
          <input className="bp-input" type="text" value={data.interest_rate} onChange={(e) => setField("interest_rate", e.target.value)} placeholder="e.g. 18% per annum" />
        </FieldRow>
        <FieldRow
          label="Estimated Monthly Installment"
          hint="Calculated from funding amount, interest rate, and repayment period."
        >
          <input
            className="bp-input bp-input--readonly"
            type="text"
            readOnly
            value={formattedMonthlyInstallment ? `${currencyPrefix}${formattedMonthlyInstallment}` : ""}
            placeholder="Estimated monthly installment — enter funding amount, rate, and period above"
          />
        </FieldRow>
      </div>
      <div className="bp-row">
        <FieldRow label="Source of Repayment">
          <input className="bp-input" type="text" value={data.repayment_source} onChange={(e) => setField("repayment_source", e.target.value)} placeholder="e.g. Monthly business revenue" />
        </FieldRow>
      </div>
      <FieldRow label="Collateral Offered">
        <input className="bp-input" type="text" value={data.collateral} onChange={(e) => setField("collateral", e.target.value)} placeholder={`e.g. Commercial property at 45 Marina Street, Lagos (valued at ${currencyPrefix}25M)`} />
      </FieldRow>
    </div>
  );

  const renderStep9 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Risk & Mitigation</h2>
      <p className="bp-step-desc">Identify key risks and your mitigation strategies. Shows lenders you&apos;ve planned ahead.</p>

      {data.risks.map((risk, idx) => (
        <LoopRow key={idx} removable={data.risks.length > 1} onRemove={() => removeListItem("risks", idx)}>
          <div className="bp-risk-fields">
            <FieldRow label="Risk Factor">
              <input className="bp-input" type="text" value={risk.risk_name} onChange={(e) => updateListItem("risks", idx, "risk_name", e.target.value)} placeholder="e.g. Fuel price volatility" />
            </FieldRow>
            <FieldRow label="Likelihood">
              <select className="bp-input" value={risk.risk_likelihood} onChange={(e) => updateListItem("risks", idx, "risk_likelihood", e.target.value)}>
                {LIKELIHOODS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Impact">
              <select className="bp-input" value={risk.risk_impact} onChange={(e) => updateListItem("risks", idx, "risk_impact", e.target.value)}>
                {IMPACTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </FieldRow>
            <div className="bp-field">
              <div className="bp-label-row">
                <label className="bp-label">Mitigation Strategy</label>
                <button
                  className="bp-ai-btn"
                  type="button"
                  disabled={aiLoading[`risk_${idx}`] || !(risk.risk_name || "").trim()}
                  onClick={() => handleRiskMitigationSuggest(idx)}
                >
                  {aiLoading[`risk_${idx}`] ? "✦ Suggesting..." : "✦ Get Suggestions"}
                </button>
              </div>
              <input
                className="bp-input"
                type="text"
                value={risk.risk_mitigation}
                onChange={(e) => updateListItem("risks", idx, "risk_mitigation", e.target.value)}
                placeholder="e.g. Fuel hedging contracts, route optimization"
              />
            </div>
          </div>
        </LoopRow>
      ))}
      <button className="bp-add-btn" onClick={() => addListItem("risks", emptyRisk)} type="button">+ Add Risk</button>
    </div>
  );

  const renderStep10 = () => (
    <div className="bp-step">
      <h2 className="bp-step-title">Suggestions & Generate</h2>
      <p className="bp-step-desc">
        Generate all narrative sections with one click, or refine them individually. Review before generating the final document.
      </p>

      <button className="bp-draft-all-btn" onClick={handleDraftAll} disabled={aiLoading.all} type="button">
        {draftAllProgress.active
          ? `✦ Drafting... (${draftAllProgress.completed}/${draftAllProgress.total} complete)`
          : "✦ Draft All Narrative Sections"}
      </button>

      {aiDraftError && (
        <div className="bp-error-banner">
          Suggestions failed. You can fill the sections manually or try again.
        </div>
      )}

      <div className="bp-narratives-grid">
        <NarrativeFieldRow label="Executive Summary" value={data.executive_summary} onChange={(v) => setField("executive_summary", v)} rows={5} loading={aiLoading.draft_executive_summary} error={aiDraftErrors.executive_summary} />
        <NarrativeFieldRow label="Business Description" hint="Brief overview of what the business does." value={data.business_description} onChange={(v) => setField("business_description", v)} rows={4} loading={aiLoading.draft_business_description} error={aiDraftErrors.business_description} />
        <NarrativeFieldRow label="Mission Statement" value={data.mission_statement} onChange={(v) => setField("mission_statement", v)} rows={2} loading={aiLoading.draft_mission_statement} error={aiDraftErrors.mission_statement} />
        <NarrativeFieldRow label="Vision Statement" value={data.vision_statement} onChange={(v) => setField("vision_statement", v)} rows={2} loading={aiLoading.draft_vision_statement} error={aiDraftErrors.vision_statement} />
        <NarrativeFieldRow label="Industry Trends" value={data.industry_trends} onChange={(v) => setField("industry_trends", v)} rows={4} loading={aiLoading.draft_industry_trends} error={aiDraftErrors.industry_trends} />
        <NarrativeFieldRow label="Competitive Landscape" value={data.competitive_landscape} onChange={(v) => setField("competitive_landscape", v)} rows={4} loading={aiLoading.draft_competitive_landscape} error={aiDraftErrors.competitive_landscape} />
        <NarrativeFieldRow label="Competitive Advantage" value={data.competitive_advantage} onChange={(v) => setField("competitive_advantage", v)} rows={3} loading={aiLoading.draft_competitive_advantage} error={aiDraftErrors.competitive_advantage} />

        <div className="bp-swot-grid">
          <div className="bp-swot-label">SWOT Analysis</div>
          <NarrativeFieldRow label="Strengths" value={data.strengths} onChange={(v) => setField("strengths", v)} rows={4} loading={aiLoading.draft_strengths} error={aiDraftErrors.strengths} />
          <NarrativeFieldRow label="Weaknesses" value={data.weaknesses} onChange={(v) => setField("weaknesses", v)} rows={4} loading={aiLoading.draft_weaknesses} error={aiDraftErrors.weaknesses} />
          <NarrativeFieldRow label="Opportunities" value={data.opportunities} onChange={(v) => setField("opportunities", v)} rows={4} loading={aiLoading.draft_opportunities} error={aiDraftErrors.opportunities} />
          <NarrativeFieldRow label="Threats" value={data.threats} onChange={(v) => setField("threats", v)} rows={4} loading={aiLoading.draft_threats} error={aiDraftErrors.threats} />
        </div>

        <NarrativeFieldRow label="Funding Justification" value={data.funding_justification} onChange={(v) => setField("funding_justification", v)} rows={4} loading={aiLoading.draft_funding_justification} error={aiDraftErrors.funding_justification} />
        <NarrativeFieldRow label="Financial Assumptions" value={data.financial_assumptions} onChange={(v) => setField("financial_assumptions", v)} rows={4} loading={aiLoading.draft_financial_assumptions} error={aiDraftErrors.financial_assumptions} />
        <NarrativeFieldRow label="Repayment Strategy" value={data.repayment_strategy} onChange={(v) => setField("repayment_strategy", v)} rows={4} loading={aiLoading.draft_repayment_strategy} error={aiDraftErrors.repayment_strategy} />
        <NarrativeFieldRow label="Risk Introduction" value={data.risk_intro} onChange={(v) => setField("risk_intro", v)} rows={3} loading={aiLoading.draft_risk_intro} error={aiDraftErrors.risk_intro} />
        <NarrativeFieldRow label="Closing Statement" value={data.closing_statement} onChange={(v) => setField("closing_statement", v)} rows={4} loading={aiLoading.draft_closing_statement} error={aiDraftErrors.closing_statement} />
      </div>

      {showReviewModal && (
        <div className="bp-confirm-dialog bp-review-modal">
          <h3 className="bp-review-title">Review Before Generating</h3>
          <p className="bp-review-subtitle">Please confirm your key details before generating the document.</p>

          <div className="bp-review-grid">
            <div className="bp-review-row">
              <span className="bp-review-label">Business Name</span>
              <span className="bp-review-value">{data.business_name || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Industry</span>
              <span className="bp-review-value">{resolvedIndustry || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Business Stage</span>
              <span className="bp-review-value">{data.business_stage || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Prepared For</span>
              <span className="bp-review-value">{data.prepared_for || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Funding Requested</span>
              <span className="bp-review-value">{currencyPrefix}{data.funding_amount || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Funding Purpose</span>
              <span className="bp-review-value">{data.funding_purpose || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Currency</span>
              <span className="bp-review-value">{data.currency || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Current Revenue</span>
              <span className="bp-review-value">{currencyPrefix}{data.current_revenue || <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">Repayment Period</span>
              <span className="bp-review-value">{data.repayment_period ? `${data.repayment_period} months` : <em>Not set</em>}</span>
            </div>
            <div className="bp-review-row">
              <span className="bp-review-label">CEO / Signatory</span>
              <span className="bp-review-value">{data.signatoryName || <em>Not set</em>}</span>
            </div>
          </div>

          <p className="bp-review-note">To change any detail, close this dialog and navigate to the relevant step.</p>

          <div className="bp-confirm-actions">
            <button className="bp-btn bp-btn--secondary" type="button" onClick={() => setShowReviewModal(false)}>
              Go Back
            </button>
            <button className="bp-btn bp-btn--primary" type="button" onClick={handleReviewConfirm}>
              Confirm & Generate
            </button>
          </div>
        </div>
      )}

      {showEmptyNarrativeConfirm && (
        <div className="bp-confirm-dialog">
          <p>Some narrative sections are empty. The document will be generated with blank sections. Continue?</p>
          <div className="bp-confirm-actions">
            <button className="bp-btn bp-btn--secondary" type="button" onClick={() => setShowEmptyNarrativeConfirm(false)}>No</button>
            <button className="bp-btn bp-btn--primary" type="button" onClick={doGenerate}>Yes</button>
          </div>
        </div>
      )}

      <div className="bp-generate-bar">
        <div className="bp-credits-note">This document costs <strong>20 credits</strong></div>
        {generating && generationStatusMessage && (
          <p className="bp-generation-status">{generationStatusMessage}</p>
        )}
        {generationError && (
          <p className="bp-generation-error" role="alert">{generationError}</p>
        )}
        {documentGenerated && generationJobId ? (
          <button
            className="bp-generate-btn bp-generate-btn--download"
            onClick={handlePdfDownload}
            disabled={downloadingPdf}
            type="button"
            data-testid="download-business-plan-btn"
          >
            {downloadingPdf ? "Downloading…" : "⬇ Download Business Plan PDF"}
          </button>
        ) : (
          <button className="bp-generate-btn" onClick={handleGenerate} disabled={generating || rerunRestoring} type="button">
            {rerunRestoring
              ? "Loading previous plan..."
              : generating
                ? "Generating..."
                : "⬇ Generate Business Plan PDF"}
          </button>
        )}
        {documentGenerated && (
          <p className="bp-download-ready-note">Your business plan is ready. Click the button above to download.</p>
        )}
      </div>
    </div>
  );

  const stepRenderers = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
    5: renderStep5,
    6: renderStep6,
    7: renderStep7,
    8: renderStep8,
    9: renderStep9,
    10: renderStep10,
  };

  return (
    <div className="bp-page">
      <div className="bp-header">
        <button className="bp-back-btn" onClick={() => navigate("/documents/business-proposal/categories")} type="button">← Back</button>
        <div className="bp-header-title">
          <h1>Business Plan & Funding Proposal</h1>
          <p>Complete all steps to generate a lender-ready business plan</p>
        </div>
      </div>

      <ProposalStepIndicator steps={STEPS} step={step} />

      {showDraftPill && (
        <div className="bp-draft-pill" data-testid="draft-restored-pill">
          <span className="bp-draft-pill-text">
            <strong>Draft restored</strong> — auto-saved every 30 seconds.
          </span>
          <div className="bp-draft-pill-actions">
            <button className="bp-btn bp-btn--secondary bp-btn--sm" type="button" onClick={dismissDraftPill} data-testid="dismiss-draft-pill">
              Dismiss
            </button>
            <button className="bp-btn bp-btn--ghost bp-btn--sm bp-btn--danger" type="button" onClick={clearDraft} data-testid="clear-draft-btn">
              Reset draft
            </button>
          </div>
        </div>
      )}

      <div className="bp-content">
        {stepRenderers[step]()}
      </div>

      <div className="bp-nav">
        {step > 1 && (
          <button className="bp-btn bp-btn--secondary" onClick={prev} type="button">← Previous</button>
        )}
        {step < WIZARD_STEP_COUNT && (
          <button className="bp-btn bp-btn--primary" onClick={next} type="button">Next →</button>
        )}
      </div>
    </div>
  );
}
